import { useCallback, useEffect, useState } from "react";
import useApiClient from "./useApiClient";
import { Geometry, Position, BBox } from "geojson";
import { Center } from "../components/SearchLocation/SearchLocation";
import { CRS_PL1992, CRS_WGS84, getProj4 as getProj4FromCentral } from "../map/projections";

// wkx + buffer nadal lazy, żeby nie wciągać ich do głównego chunku.
let _wkx: any = null;
let _Buffer: any = null;

function getProj4() {
  return getProj4FromCentral();
}

async function getWkx() {
  if (!_wkx) {
    // KRYTYCZNA KOLEJNOŚĆ: Buffer MUSI być ustawiony na globalThis ZANIM zaimportujemy
    // wkx. wkx w module binarywriter.js na top-levelu robi `new Buffer(...)` (stack z
    // binarywriter.js:18). Jeżeli ustawimy Buffer dopiero po `import("wkx")`, wkx już
    // padł przy swojej inicjalizacji.
    _Buffer = (await import("buffer")).Buffer;
    if (typeof (globalThis as any).Buffer === "undefined") {
      (globalThis as any).Buffer = _Buffer;
    }
    _wkx = (await import("wkx")).default;
  }
  return { wkx: _wkx, Buffer: _Buffer };
}

function findBboxCenter(bbox: BBox): { lat: number; lng: number } {
  const center: { lat: number; lng: number } = {
    lng: bbox[0] + (bbox[1] - bbox[0]) / 2,
    lat: bbox[2] + (bbox[3] - bbox[2]) / 2,
  };
  return center;
}
const transformPoligonCoordinates = (coord: Position[][], p4: any): Position[][] => {
  // ULDK zwraca PL-1992 (2180) — transformujemy do WGS84 (4326), bo OlMap
  // reprojektuje geometrię na widok mapy (PL-2000, strefa wybrana po lokalizacji)
  // z użyciem `featureProjection` w `GeoJSON.readFeatures`.
  return coord.map((ring) => ring.map(([x, y]) => p4(CRS_PL1992, CRS_WGS84, [x, y])));
};

async function from2180to4326(wkbFormatString: string, p4: any): Promise<Geometry & Center> {
  const { wkx, Buffer } = await getWkx();
  const sourceProj = CRS_PL1992;
  const destProj = CRS_WGS84;
  const geometry = wkx.Geometry.parse(Buffer.from(wkbFormatString, "hex"));
  const geojson = geometry.toGeoJSON() as Geometry & Center;
  // Transform coordinates
  if (geojson.type === "Polygon") {
    geojson.coordinates = transformPoligonCoordinates(geojson.coordinates, p4);
    const defaultBbox: BBox = [
      geojson.coordinates[0][0][0],
      geojson.coordinates[0][0][0],
      geojson.coordinates[0][0][1],
      geojson.coordinates[0][0][1],
    ];
    geojson.bbox = geojson.coordinates.reduce<BBox>((acc: BBox, curr: Position[]) => {
      curr.forEach(([x, y]) => {
        acc[0] = Math.min(x, acc[0]);
        acc[1] = Math.max(x, acc[1]);
        acc[2] = Math.min(y, acc[2]);
        acc[3] = Math.max(y, acc[3]);
      });
      return acc;
    }, defaultBbox);
    geojson.center = findBboxCenter(geojson.bbox);
  }
  if (geojson.type === "MultiPolygon") {
    geojson.coordinates = geojson.coordinates.map((ring) => transformPoligonCoordinates(ring, p4));
    const defaultBbox: BBox = [
      geojson.coordinates[0][0][0][0],
      geojson.coordinates[0][0][0][0],
      geojson.coordinates[0][0][0][1],
      geojson.coordinates[0][0][0][1],
    ];
    geojson.bbox = geojson.coordinates.reduce<BBox>((acc: BBox, curr: Position[][]) => {
      curr.forEach((circle) => {
        circle.forEach(([x, y]) => {
          acc[0] = Math.min(x, acc[0]);
          acc[1] = Math.max(x, acc[1]);
          acc[2] = Math.min(y, acc[2]);
          acc[3] = Math.max(y, acc[3]);
        });
      });
      return acc;
    }, defaultBbox);
    geojson.center = findBboxCenter(geojson.bbox);
  } else if (geojson.type === "Point") {
    geojson.coordinates = p4(sourceProj, destProj, geojson.coordinates);
  }

  return geojson;
}

async function prepareGeometry(data: string): Promise<(Geometry & Center)[] & { bbox?: BBox }> {
  const p4 = await getProj4();
  const [status, ...response] = data.split("\n");
  if (status.includes("-1")) {
    throw new Error("Resource did not found");
  }
  const prepareData = (await Promise.all(response.filter((x) => !!x).map((wkd) => from2180to4326(wkd, p4)))) as (Geometry & Center)[] & { bbox?: BBox };
  if (prepareData.length === 1) return prepareData;
  const seedBbox: BBox = (prepareData[0] as Geometry & Center & { bbox?: BBox })?.bbox ?? [0, 0, 0, 0];
  const wholeBbox: BBox = prepareData.reduce<BBox>(([xMin, xMax, yMin, yMax]: BBox, item) => {
    const bbox = (item as Geometry & Center & { bbox?: BBox }).bbox;
    xMin = bbox?.[0] !== undefined ? Math.min(bbox[0], xMin) : xMin;
    xMax = bbox?.[1] !== undefined ? Math.max(bbox[1], xMax) : xMax;
    yMax = bbox?.[2] !== undefined ? Math.min(bbox[2], yMin) : yMin;
    yMin = bbox?.[3] !== undefined ? Math.max(bbox[3], yMax) : yMax;
    return [xMin, xMax, yMin, yMax];
  }, seedBbox);
  prepareData.bbox = wholeBbox;
  return prepareData;
}

function prepareData(data: string): Option[] {
  const list = data.split("\n");
  list.shift();
  // Filtrujemy puste linie (trailing newline, EOF) ORAZ rekordy bez separatora `|`
  // (np. gdy ULDK zwróci komunikat błędu jako pierwszą linię po shift() albo
  // niepełny rekord). Bez tego `label`/`value` mogą być `undefined`, co wywraca
  // konsumentów (np. `.includes()` na undefined → TypeError w PrecinctSuggestions).
  const prepareData = list
    .map((val) => val.trim())
    .filter((val) => val.length > 0 && val.includes("|"))
    .map((val) => {
      const [label, value] = val.split("|");
      return { value: value ?? "", label: label ?? "" };
    })
    .filter((opt) => opt.value.length > 0 && opt.label.length > 0);

  return prepareData;
}

export type Option = { label: string; value: string };

type UldkReturnType = {
  voivodeships: Option[];
  districts: Option[];
  fetchDistricts: (value: string) => Promise<void>;
  fetchTenants: (value: string) => Promise<void>;
  tenants: Option[];
  fetchPrecincts: (value: string) => Promise<void>;
  precincts: Option[];
  getRegionGeometryById: (regionId: string) => Promise<(Geometry & Center) | (Geometry & Center)[]>;
  getParcelGeometryById: (
    parcelId: string,
  ) => Promise<(Geometry & Center) | ((Geometry & Center)[] & { bbox?: BBox | undefined })>;
};
type useUldkPropsType = { voivodeship?: string; district?: string; tenant?: string; precinct?: string };

const useUldk = ({ voivodeship, district, tenant, precinct: _precinct }: useUldkPropsType): UldkReturnType => {
  const [voivodeships, setVoivodeships] = useState<Option[]>([]);
  const [districts, setDistricts] = useState<Option[]>([]);
  const [tenants, setTenants] = useState<Option[]>([]);
  const [precincts, setPrecincts] = useState<Option[]>([]);

  const { publicApiClient } = useApiClient();
  const fetchObject = useCallback(
    async ({ object, teryt }: { object: string; teryt: string }) => {
      try {
        const { data } = await publicApiClient.get(
          `https://uldk.gugik.gov.pl/service.php?obiekt=${object}&wynik=${object},teryt&teryt=${teryt}`,
        );
        return prepareData(data);
      } catch (error) {
        console.error(error);
      }
    },
    [publicApiClient],
  );
  useEffect(() => {
    publicApiClient
      .get("https://uldk.gugik.gov.pl/service.php?obiekt=wojewodztwo&wynik=wojewodztwo,teryt")
      .then(({ data }) => {
        setVoivodeships(prepareData(data) || []);
      });
  }, []);

  const fetchDistricts = useCallback(
    async (value: string) => {
      try {
        const object = await fetchObject({ object: "powiat", teryt: value });
        setDistricts(object || []);
      } catch (e) {
        console.error(e);
      }
    },
    [fetchObject],
  );
  const fetchTenants = useCallback(
    async (value: string) => {
      const object = await fetchObject({ object: "gmina", teryt: value });
      setTenants(object || []);
    },
    [fetchObject],
  );
  const fetchPrecincts = useCallback(
    async (value: string) => {
      const object = await fetchObject({ object: "obreb", teryt: value });
      setPrecincts(object || []);
    },
    [fetchObject],
  );
  const getRegionGeometryById = useCallback(
    async (regionId: string) => {
      const { data } = await publicApiClient.get(`https://uldk.gugik.gov.pl/?request=GetRegionById&id=${regionId}`);
      return prepareGeometry(data);
    },
    [publicApiClient],
  );
  const getParcelGeometryById = useCallback(
    async (parcelId: string) => {
      const { data } = await publicApiClient.get(`https://uldk.gugik.gov.pl/?request=GetParcelById&id=${parcelId}`);
      return prepareGeometry(data);
    },
    [publicApiClient],
  );
  useEffect(() => {
    voivodeship && fetchDistricts(voivodeship);
    district && fetchTenants(district);
    tenant && fetchPrecincts(tenant);
  }, [voivodeship, district, tenant, fetchDistricts, fetchTenants, fetchPrecincts]);
  return {
    voivodeships,
    districts,
    fetchDistricts,
    fetchTenants,
    tenants,
    fetchPrecincts,
    precincts,
    getRegionGeometryById,
    getParcelGeometryById,
  };
};

export default useUldk;
